import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createBooking, getPublicSettings, getAddons, getReservedDatesForProduct, getDeliveryQuote } from '../services/api';
import api from '../services/api';
import { Product, CouponApplication, Addon, DeliveryQuote } from '../types';
import { ArrowLeftIcon, CalendarIcon, DollarSignIcon, CheckIcon, CreditCardIcon, XIcon } from 'lucide-react';
import { getContent } from '../config/content';
import { theme } from '../config/theme';
import StripePaymentForm from '../components/StripePaymentForm';
import { format } from 'date-fns';
import { clearDatesFromStorage } from '../utils/dateStorage';
import AddressFields from '../components/AddressFields';
import CouponInput from '../components/CouponInput';
import { validateTextInput, validateEmail, validatePhone } from '../utils/validation';

interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
}

interface BookingState {
  productId: string;
  startDate: string;
  endDate: string;
  product: Product;
  selectedAddons?: {addonId: string, selectedOption?: {label: string, priceModifier?: number, image?: string}}[];
}

const Booking: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as BookingState;

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: ''
    },
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponApplication | null>(null);
  const [taxRate, setTaxRate] = useState(0.08); // Default 8%
  const [depositPercentage, setDepositPercentage] = useState(15); // Default 15%
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [addons, setAddons] = useState<Addon[]>([]);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string>('');
  const [payInFull, setPayInFull] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  // Pricing rules, so the summary can explain the policy before an address is
  // entered. `configured` is false until an origin is set in admin settings,
  // in which case delivery is free and the row is hidden entirely.
  const [deliveryPolicy, setDeliveryPolicy] = useState({
    configured: false,
    freeRadiusMiles: 10,
    maxRadiusMiles: 25
  });

  useEffect(() => {
    if (!state || !state.productId) {
      navigate('/rentals');
    }
  }, [state, navigate]);

  // Scroll to payment form when it appears
  useEffect(() => {
    if (showPayment) {
      // Small delay to ensure the payment form is rendered
      setTimeout(() => {
        const paymentElement = document.getElementById('payment-form-section');
        if (paymentElement) {
          paymentElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [showPayment]);

  useEffect(() => {
    const fetchAddonsData = async () => {
      try {
        const data = await getAddons();
        setAddons(data);
      } catch (error) {
        console.error('Error fetching addons:', error);
      }
    };
    
    fetchAddonsData();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getPublicSettings();
        if (settings.tax_rate) {
          setTaxRate(settings.tax_rate / 100);
        }
        if (settings.deposit_percentage) {
          setDepositPercentage(settings.deposit_percentage);
        }
        setDeliveryPolicy({
          configured: Boolean(settings.delivery_configured),
          freeRadiusMiles: Number(settings.delivery_free_radius_miles ?? 10),
          maxRadiusMiles: Number(settings.delivery_max_radius_miles ?? 25)
        });
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  const getAddonDetails = (addonId: string) => {
    return addons.find(a => a.addon_id === addonId);
  };

  const calculateAddonsTotal = () => {
    if (!state.selectedAddons || state.selectedAddons.length === 0) return 0;
    
    return state.selectedAddons.reduce((total, selectedAddon) => {
      const addon = getAddonDetails(selectedAddon.addonId);
      if (!addon) return total;
      
      // Addon prices are stored in cents, convert to dollars for display
      const basePrice = parseFloat(String(addon.price)) / 100;
      const optionModifier = selectedAddon.selectedOption?.priceModifier || 0; // Already in dollars
      return total + basePrice + optionModifier;
    }, 0);
  };

  // Quote the delivery fee once the address is complete enough to geocode.
  const { line1: addrLine1, line2: addrLine2, city: addrCity, state: addrState, postal_code: addrZip } = formData.delivery_address;
  const addressIsComplete = Boolean(
    addrLine1.trim() && addrCity.trim() && addrState.trim() && /^\d{5}$/.test(addrZip.trim())
  );

  useEffect(() => {
    if (!addressIsComplete) {
      setDeliveryQuote(null);
      setDeliveryError('');
      return;
    }

    let cancelled = false;
    setDeliveryLoading(true);
    setDeliveryError('');

    const timer = setTimeout(async () => {
      try {
        const quote = await getDeliveryQuote({
          line1: addrLine1,
          line2: addrLine2,
          city: addrCity,
          state: addrState,
          postal_code: addrZip
        });
        if (!cancelled) setDeliveryQuote(quote);
      } catch (err: any) {
        if (cancelled) return;
        setDeliveryQuote(null);
        // 503 means no delivery origin is configured yet - treat delivery as
        // free rather than blocking checkout on an admin misconfiguration.
        if (err.response?.status !== 503) {
          setDeliveryError(
            err.response?.data?.detail || 'We could not calculate a delivery fee for this address.'
          );
        }
      } finally {
        if (!cancelled) setDeliveryLoading(false);
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addressIsComplete, addrLine1, addrLine2, addrCity, addrState, addrZip]);

  // Cleanup effect to clear booking state when navigating away
  useEffect(() => {
    return () => {
      // Clear dates from storage when navigating away
      clearDatesFromStorage();
    };
  }, []);

  const handleCouponApplied = (application: CouponApplication) => {
    setAppliedCoupon(application);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
  };

  const calculateDays = () => {
    return Math.ceil((new Date(state.endDate).getTime() - new Date(state.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1; // Include both start and end days
  };

  // Delivery fee is quoted by the server from the driving distance to the
  // delivery address. Coupons don't discount it, and it is taxed with the rest.
  const calculateDeliveryFee = () => {
    return deliveryQuote?.status === 'fee' ? deliveryQuote.delivery_fee_cents / 100 : 0;
  };

  const deliveryOutOfRange = deliveryQuote?.status === 'out_of_range';

  const calculateTotalPrice = () => {
    const basePrice = state.product.price ? (state.product.price.amount_cents / 100) : 0;
    const days = calculateDays();
    const addonsTotal = calculateAddonsTotal();
    const subtotal = basePrice * days + addonsTotal;
    const discountedSubtotal = appliedCoupon ? Math.max(0, subtotal - appliedCoupon.discount_amount) : subtotal;
    const taxableSubtotal = discountedSubtotal + calculateDeliveryFee();
    const tax = taxableSubtotal * taxRate;
    const totalPrice = taxableSubtotal + tax;
    return totalPrice;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double-clicks by setting loading immediately
    if (loading) return;
    setLoading(true);
    setError('');
    
    // Validate all fields before proceeding
    const errors: Record<string, string> = {};
    
    // Validate name
    const nameValidation = validateTextInput(formData.customer_name, {
      minLength: 2,
      maxLength: 100,
      allowSpaces: true,
      allowNumbers: false,
      allowSpecialChars: false
    });
    if (!nameValidation.isValid) {
      errors.customer_name = nameValidation.error || 'Invalid name';
    }
    
    // Validate email
    const emailValidation = validateEmail(formData.customer_email);
    if (!emailValidation.isValid) {
      errors.customer_email = emailValidation.error || 'Invalid email format';
    }
    
    // Validate phone
    const phoneValidation = validatePhone(formData.customer_phone);
    if (!phoneValidation.isValid) {
      errors.customer_phone = phoneValidation.error || 'Phone must be in format XXX-XXX-XXXX';
    }
    
    // Validate terms acceptance
    if (!acceptedTerms) {
      errors.terms = 'You must accept the terms and conditions to proceed';
    }

    setValidationErrors(errors);

    // If there are errors, don't proceed
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors before proceeding');
      setLoading(false); // Reset loading state
      return;
    }

    // Addresses beyond the delivery radius can't be booked online.
    if (deliveryOutOfRange) {
      setError(deliveryQuote?.message || 'This address is outside our delivery area.');
      setLoading(false);
      return;
    }

    // Don't let the customer pay against a stale total while a quote is in flight.
    if (deliveryLoading) {
      setError('Still calculating your delivery fee. Please try again in a moment.');
      setLoading(false);
      return;
    }

    try {
      // Check for conflicts using reserved dates API before proceeding to payment
      const startDateUTC = state.startDate;
      const endDateUTC = state.endDate;
      
      // Fetch reserved dates for the selected date range
      const reservedDates = await getReservedDatesForProduct(state.productId, startDateUTC, endDateUTC);
      
      // Check if any dates in the selected range are now reserved
      const dateRange: string[] = [];
      const start = new Date(startDateUTC);
      const end = new Date(endDateUTC);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = format(d, 'yyyy-MM-dd');
        dateRange.push(dateStr);
      }
      
      // Check for conflicts
      const hasConflicts = reservedDates.some((reservedDate: string) => {
        return dateRange.includes(reservedDate);
      });
      
      if (hasConflicts) {
        throw new Error('The dates selected are no longer available. Please select a new rental date');
      }
      
      // Check if coupon provides 100% discount (total price is $0)
      const totalPrice = calculateTotalPrice();
      if (totalPrice === 0) {
        // Skip payment and create booking directly for free bookings
        await createFreeBooking();
      } else {
        // If no conflicts and payment required, show payment form
        setShowPayment(true);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to check availability');
    } finally {
      setLoading(false);
    }
  };

  const createFreeBooking = async () => {
    // Create booking without payment when total is $0 (100% discount)
    // Server will validate the coupon and ensure total is actually $0
    try {
      const totalPrice = calculateTotalPrice();
      const startDateUTC = state.startDate;
      const endDateUTC = state.endDate;
      
      const bookingData = {
        product_id: state.productId,
        start_date: startDateUTC,
        end_date: endDateUTC,
        customer: {
          full_name: formData.customer_name,
          email: formData.customer_email,
          phone: formData.customer_phone
        },
        delivery_address: formData.delivery_address,
        notes: formData.notes,
        coupon_id: appliedCoupon?.coupon_id,
        coupon_code: appliedCoupon?.coupon_code,
        discount_amount: Math.round((appliedCoupon?.discount_amount || 0) * 100), // Convert to cents
        discount_type: appliedCoupon?.discount_type || null,
        discount_value: appliedCoupon?.discount_value || 0,
        delivery_fee: Math.round(calculateDeliveryFee() * 100), // Convert to cents
        delivery_distance_miles: deliveryQuote?.distance_miles ?? null,
        payment_intent_id: null, // No payment required
        deposit_amount: 0, // No deposit for free booking
        paid_amount: 0, // No payment for free booking
        total_price: Math.round(totalPrice * 100), // Convert to cents (should be 0)
        addons: state.selectedAddons?.map(selectedAddon => {
          const addon = getAddonDetails(selectedAddon.addonId);
          return {
            addon_id: selectedAddon.addonId,
            quantity: 1,
            selected_option: selectedAddon.selectedOption?.label || selectedAddon.selectedOption || null
          };
        }) || []
      };

      await createBooking(bookingData);
      setSuccess(true);
      clearDatesFromStorage();
    } catch (error: any) {
      // Handle 409 conflicts with user-friendly message
      if (error.response?.status === 409 || error.response?.data?.error === 'time_conflict') {
        setError('The dates selected are no longer available. Please select a new rental date');
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to create booking');
      }
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  const handlePaymentSuccess = async (intentId: string) => {
    setPaymentIntentId(intentId);
    setLoading(true);
    setError('');

    try {
      // Payment succeeded, proceed directly to booking creation
      const totalPrice = calculateTotalPrice();
      const depositAmount = payInFull ? totalPrice : totalPrice * (depositPercentage / 100);
      
      // Use dates directly as UTC dates - no conversion needed
      const startDateUTC = state.startDate;
      const endDateUTC = state.endDate;
      
      const bookingData = {
        product_id: state.productId,
        start_date: startDateUTC,
        end_date: endDateUTC,
        customer: {
          full_name: formData.customer_name,
          email: formData.customer_email,
          phone: formData.customer_phone
        },
        delivery_address: formData.delivery_address,
        notes: formData.notes,
        coupon_id: appliedCoupon?.coupon_id,
        coupon_code: appliedCoupon?.coupon_code,
        discount_amount: Math.round((appliedCoupon?.discount_amount || 0) * 100), // Convert to cents
        discount_type: appliedCoupon?.discount_type || null,
        discount_value: appliedCoupon?.discount_value || 0,
        delivery_fee: Math.round(calculateDeliveryFee() * 100), // Convert to cents
        delivery_distance_miles: deliveryQuote?.distance_miles ?? null,
        payment_intent_id: intentId,
        deposit_amount: Math.round(depositAmount * 100), // Convert to cents
        paid_amount: Math.round(depositAmount * 100), // Convert to cents - same as deposit for initial payment
        total_price: Math.round(totalPrice * 100), // Convert to cents
        addons: state.selectedAddons?.map(selectedAddon => {
          const addon = getAddonDetails(selectedAddon.addonId);
          // Don't send price field - backend will fetch it from addon data
          return {
            addon_id: selectedAddon.addonId,
            quantity: 1,
            selected_option: selectedAddon.selectedOption?.label || selectedAddon.selectedOption || null
          };
        }) || []
      };

      await createBooking(bookingData);
      setSuccess(true);
      clearDatesFromStorage();
    } catch (error: any) {
      // Handle 409 conflicts with user-friendly message
      if (error.response?.status === 409 || error.response?.data?.error === 'time_conflict') {
        setError('The dates selected are no longer available. Please select a new rental date');
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to create booking');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
    setLoading(false);
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Format as XXX-XXX-XXXX
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let error = '';
    let processedValue = value;
    
    // Validate based on field type
    switch (field) {
      case 'customer_name':
        const nameValidation = validateTextInput(value, {
          minLength: 2,
          maxLength: 100,
          allowSpaces: true,
          allowNumbers: false,
          allowSpecialChars: false
        });
        if (!nameValidation.isValid) {
          error = nameValidation.error || 'Invalid input';
        }
        break;
        
      case 'customer_email':
        const emailValidation = validateEmail(value);
        if (value && !emailValidation.isValid) {
          error = emailValidation.error || 'Invalid email format';
        }
        break;
        
      case 'customer_phone':
        // Format phone number with dashes
        processedValue = formatPhoneNumber(value);
        const phoneValidation = validatePhone(processedValue);
        if (processedValue && !phoneValidation.isValid) {
          error = phoneValidation.error || 'Phone must be in format XXX-XXX-XXXX';
        }
        break;
        
      case 'notes':
        // Allow alphanumeric characters, spaces, and common punctuation
        processedValue = value.replace(/[^a-zA-Z0-9\s?,.!$%()'"]/g, '');
        if (processedValue.length > 500) {
          error = 'Notes must be 500 characters or less';
        }
        break;
    }
    
    setValidationErrors({ ...validationErrors, [field]: error });
    setFormData({ ...formData, [field]: processedValue });
  };

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckIcon className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-6">
              Your booking for {state.product.name} has been successfully created.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Product:</strong> {state.product.name}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Dates:</strong> {format(new Date(state.startDate + 'T00:00:00'), 'MMM d, yyyy')} - {format(new Date(state.endDate + 'T00:00:00'), 'MMM d, yyyy')}
              </p>
              {calculateDeliveryFee() > 0 && (
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Delivery Fee:</strong> ${calculateDeliveryFee().toFixed(2)}
                  {deliveryQuote && (
                    <span className="text-gray-500 ml-1">({deliveryQuote.distance_miles} mi)</span>
                  )}
                </p>
              )}
              <p className="text-sm text-gray-600 mb-2">
                <strong>Total Price:</strong> ${calculateTotalPrice().toFixed(2)}
                {appliedCoupon && (
                  <span className="text-green-600 ml-2">
                    (Saved ${appliedCoupon.discount_amount.toFixed(2)})
                  </span>
                )}
              </p>
              {payInFull ? (
                <p className="text-sm text-gray-600">
                  <strong>Total Paid:</strong> ${calculateTotalPrice().toFixed(2)} (Paid in Full)
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Total Paid:</strong> ${(calculateTotalPrice() * (depositPercentage / 100)).toFixed(2)} (Deposit)
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Due Later:</strong> ${(calculateTotalPrice() * (1 - depositPercentage / 100)).toFixed(2)}
                  </p>
                </>
              )}
            </div>
            <button
              onClick={() => navigate('/home')}
              className="w-full text-white py-2 px-4 rounded-md transition-colors"
              style={{ backgroundColor: theme.colors.primary[800] }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Back to Rentals
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: theme.colors.background.secondary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/rentals')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Rentals
        </button>

        {/* Booking Summary */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Booking Summary</h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start gap-3 mb-2">
              <span className="text-gray-600 flex-shrink-0">{getContent('booking.summary.product')}:</span>
              <span className="font-medium min-w-0 text-right break-words">{state.product.name}</span>
            </div>
            <div className="flex justify-between items-start gap-3 mb-2">
              <span className="text-gray-600 flex-shrink-0">{getContent('booking.summary.dailyRate')}:</span>
              <span className="font-medium">${state.product.price ? (state.product.price.amount_cents / 100) : 0}</span>
            </div>
            <div className="flex justify-between items-start gap-3 mb-2">
              <span className="text-gray-600 flex-shrink-0">{getContent('booking.summary.rentalPeriod')}:</span>
              <span className="font-medium min-w-0 text-right">
                {state.startDate === state.endDate 
                  ? format(new Date(state.startDate + 'T00:00:00'), 'MMM d, yyyy')
                  : `${format(new Date(state.startDate + 'T00:00:00'), 'MMM d, yyyy')} - ${format(new Date(state.endDate + 'T00:00:00'), 'MMM d, yyyy')}`
                }
              </span>
            </div>
            <div className="flex justify-between items-start gap-3">
              <span className="text-gray-600 flex-shrink-0">Rental Subtotal:</span>
              <span className="font-medium">${((state.product.price ? (state.product.price.amount_cents / 100) : 0) * calculateDays()).toFixed(2)}</span>
            </div>
            {state.selectedAddons && state.selectedAddons.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-2">Selected Add-ons:</div>
                {state.selectedAddons.map((selectedAddon, index) => {
                  const addon = getAddonDetails(selectedAddon.addonId);
                  if (!addon) return null;
                  
                  // Addon prices are in cents, convert to dollars for display
                  const basePrice = parseFloat(String(addon.price)) / 100;
                  const optionModifier = selectedAddon.selectedOption?.priceModifier || 0;
                  const totalPrice = basePrice + optionModifier;
                  
                  return (
                    <div key={index} className="flex justify-between items-center text-sm mb-1">
                      <span className="text-gray-600">
                        {addon.name}
                        {selectedAddon.selectedOption && (
                          <span className="text-gray-500"> - {selectedAddon.selectedOption.label}</span>
                        )}
                      </span>
                      <span className="font-medium">
                        ${totalPrice.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">${(((state.product.price ? (state.product.price.amount_cents / 100) : 0) * calculateDays()) + calculateAddonsTotal()).toFixed(2)}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between items-center text-green-600">
                <span className="text-sm">Discount:</span>
                <span className="font-medium">-${appliedCoupon.discount_amount.toFixed(2)}</span>
              </div>
            )}

            {/* Delivery fee - quoted from the driving distance to the address below.
                Hidden entirely when no delivery origin is configured, since then
                no fee can ever apply and the row would just confuse. */}
            {deliveryPolicy.configured && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Delivery:</span>
                {deliveryLoading ? (
                  <span className="text-sm text-gray-500 flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                    Calculating...
                  </span>
                ) : deliveryOutOfRange && deliveryQuote ? (
                  <span className="text-right">
                    <span className="font-medium text-amber-700">Contact us</span>
                    <span className="block text-xs text-amber-700">
                      {deliveryQuote.distance_miles} mi &middot; outside booking range
                    </span>
                  </span>
                ) : deliveryQuote ? (
                  <span className="font-medium">
                    {calculateDeliveryFee() > 0 ? (
                      `$${calculateDeliveryFee().toFixed(2)}`
                    ) : (
                      <span className="text-green-600">FREE</span>
                    )}
                    <span className="text-xs text-gray-500 ml-2">({deliveryQuote.distance_miles} mi)</span>
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 text-right">
                    Free within {deliveryPolicy.freeRadiusMiles} mi
                    <span className="block text-xs">
                      small fee beyond that
                    </span>
                  </span>
                )}
              </div>
            )}

            <div className="flex justify-between items-start gap-3">
              <span className="text-gray-600 flex-shrink-0">Tax ({(taxRate * 100).toFixed(1)}%):</span>
              <span className="font-medium">${(((((state.product.price ? (state.product.price.amount_cents / 100) : 0) * calculateDays() + calculateAddonsTotal()) - (appliedCoupon?.discount_amount || 0)) + calculateDeliveryFee()) * taxRate).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-lg font-bold" style={{ color: theme.colors.primary[700] }}>
                ${calculateTotalPrice().toFixed(2)}
              </span>
            </div>
            
            {/* Payment Options - Only show when total > $0 */}
            {calculateTotalPrice() > 0 && (
              <div className="mt-4 pt-4 border-t-2 border-gray-300">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Payment Option</h3>
                
                <div className="space-y-3">
                {/* Pay Deposit Option */}
                <label className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !payInFull 
                    ? 'shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                style={!payInFull ? { borderColor: theme.colors.primary.radio.selected, backgroundColor: '#dbeafe', borderWidth: '2px' } : { borderWidth: '2px' }}>
                  <input
                    type="radio"
                    name="paymentOption"
                    checked={!payInFull}
                    onChange={() => setPayInFull(false)}
                    className="mt-1 w-4 h-4 border-gray-300"
                    style={{ accentColor: theme.colors.primary.radio.selected }}
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          Pay Deposit ({depositPercentage}%)
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Pay remaining balance later
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: theme.colors.primary[700] }}>
                          ${(calculateTotalPrice() * (depositPercentage / 100)).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">due today</div>
                      </div>
                    </div>
                    {!payInFull && (
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.colors.secondary[200] }}>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Balance Due Later:</span>
                          <span className="font-semibold text-gray-900">
                            ${(calculateTotalPrice() * (1 - depositPercentage / 100)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                {/* Pay in Full Option */}
                <label className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  payInFull 
                    ? 'shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                style={payInFull ? { borderColor: theme.colors.primary.radio.selected, backgroundColor: '#dbeafe', borderWidth: '2px' } : { borderWidth: '2px' }}>
                  <input
                    type="radio"
                    name="paymentOption"
                    checked={payInFull}
                    onChange={() => setPayInFull(true)}
                    className="mt-1 w-4 h-4 border-gray-300"
                    style={{ accentColor: theme.colors.primary.radio.selected }}
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          Pay in Full (100%)
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Complete payment today - no balance due
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: theme.colors.primary[700] }}>
                          ${calculateTotalPrice().toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">due today</div>
                      </div>
                    </div>
                  </div>
                </label>
                </div>
              </div>
            )}
            
            {/* Coupon Code Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <CouponInput
                amount={((state.product.price ? (state.product.price.amount_cents / 100) : 0) * calculateDays()) + calculateAddonsTotal()}
                onCouponApplied={handleCouponApplied}
                onCouponRemoved={handleCouponRemoved}
              />
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {!showPayment ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{getContent('booking.title')}</h2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {getContent('booking.form.fullName')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    validationErrors.customer_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                />
                {validationErrors.customer_name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.customer_name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {getContent('booking.form.emailAddress')} *
                </label>
                <input
                  type="email"
                  required
                  value={formData.customer_email}
                  onChange={(e) => handleInputChange('customer_email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    validationErrors.customer_email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                />
                {validationErrors.customer_email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.customer_email}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getContent('booking.form.phoneNumber')} *
              </label>
              <input
                type="tel"
                required
                value={formData.customer_phone}
                onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  validationErrors.customer_phone ? 'border-red-500' : 'border-gray-300'
                }`}
                style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
              />
              {validationErrors.customer_phone && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.customer_phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getContent('booking.form.deliveryAddress')} *
              </label>
              <AddressFields
                value={formData.delivery_address}
                onChange={(address: Address) => setFormData({ ...formData, delivery_address: address })}
                required
              />
              {validationErrors.delivery_address && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.delivery_address}</p>
              )}

              {/* Delivery distance feedback */}
              {deliveryOutOfRange && deliveryQuote && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-4">
                  <p className="text-sm text-amber-900 font-medium mb-1">
                    This address is outside our online booking area
                  </p>
                  <p className="text-sm text-amber-800">{deliveryQuote.message}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="mt-3 inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md"
                    style={{ backgroundColor: theme.colors.primary[800] }}
                  >
                    Go to Contact Form
                  </button>
                </div>
              )}

              {!deliveryOutOfRange && deliveryQuote && (
                <p className="mt-2 text-sm text-gray-600">{deliveryQuote.message}</p>
              )}

              {deliveryError && (
                <p className="mt-2 text-sm text-amber-700">{deliveryError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getContent('booking.form.notes')}
              </label>
              <textarea
                rows={4}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder={getContent('booking.form.notesPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
              />
            </div>

            {/* Terms and Conditions */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (e.target.checked) {
                      setValidationErrors({ ...validationErrors, terms: '' });
                    }
                  }}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  style={{ accentColor: theme.colors.primary.radio.selected }}
                />
                <label htmlFor="terms" className="ml-3 text-sm text-gray-700">
                  I accept the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="font-medium hover:underline"
                    style={{ color: theme.colors.primary.radio.selected }}
                  >
                    Terms and Conditions
                  </button>
                  {' '}*
                </label>
              </div>
              {validationErrors.terms && (
                <p className="mt-2 text-sm text-red-600">{validationErrors.terms}</p>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || deliveryOutOfRange}
                style={{ backgroundColor: deliveryOutOfRange ? undefined : theme.colors.primary[800] }}
                className="flex-1 text-white py-3 px-4 rounded-md font-medium hover:opacity-90 transition-colors disabled:bg-gray-300 disabled:text-gray-500 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : deliveryOutOfRange ? (
                  'Outside Delivery Area'
                ) : calculateTotalPrice() === 0 ? (
                  <>
                    <CheckIcon className="h-5 w-5 mr-2" />
                    Complete Booking
                  </>
                ) : (
                  <>
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border rounded-md font-medium transition-colors"
                style={{ 
                  borderColor: theme.colors.secondary[300], 
                  color: theme.colors.secondary[700],
                  backgroundColor: 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.secondary[50]}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Terms and Conditions Modal */}
          {showTerms && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
                  <button
                    onClick={() => setShowTerms(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                  <div className="prose prose-sm max-w-none">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bounce Dads ATL (LLH Party Rentals) Bounce House Rental Terms and Conditions</h3>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">1. Rental Agreement Overview</h4>
                    <p className="text-gray-700 mb-4">This Rental Agreement ("Agreement") is entered into by and between the Renter ("Customer") and Bounce Dads ATL (LLH Party Rentals) ("Company") for the rental of inflatable bounce house equipment. By agreeing to this agreement, Customer agrees to abide by all terms and conditions herein.</p>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">2. Rental Period</h4>
                    <p className="text-gray-700 mb-4">The rental period listed at checkout is the agreed upon rental day(s). Any extension of the rental period must be approved by the Company and may incur additional charges.</p>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">3. Delivery and Setup</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Company will deliver, set up, and take down the equipment.</li>
                      <li>Customer must ensure clear access to the setup location.</li>
                      <li>Equipment must be set up on a flat, clean, and grassy (preferred) or smooth surface free of debris, sharp objects, or animal waste.</li>
                      <li>Electrical outlet must be available within 100 feet of set up location.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">4. Payment Terms</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Reservation deposit (15%) is due at checkout</li>
                      <li>Remaining payment is due before or at the time of delivery via invoice.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">5. Cancellation Policy</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Cancellations requests received within 48 hours (with event further than 48 hours out) will receive a full refund.</li>
                      <li>Cancellations after 48 hours of booking will receive a refund as credit for future use. Credit is good for up to 1 year.</li>
                      <li>In case of inclement weather (e.g., high winds, heavy rain), the Company may cancel the rental at its discretion for safety reasons, and a full refund will be provided.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">6. Safety Rules</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Adult supervision is required at all times for children less than 18.</li>
                      <li>No shoes, food, drinks, sharp objects, pets, or rough play inside the bounce house.</li>
                      <li>Do not exceed the maximum number of users or weight limit posted on the unit.</li>
                      <li>Users must not climb or hang on the walls or netting.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">7. Customer Responsibilities</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Ensure the bounce house remains clean and free of debris.</li>
                      <li>Keep the blower and power cords dry and protected.</li>
                      <li>Notify the Company immediately of any damage or malfunction.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">8. Damage and Liability</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Customer is responsible for any damage (other than normal wear and tear) caused by misuse, negligence, or violation of these terms.</li>
                      <li>Customer assumes full responsibility for all persons using the equipment.</li>
                      <li>Company is not liable for any injury or damages arising from improper use or lack of supervision.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">9. Weather Policy</h4>
                    <ul className="list-disc pl-6 text-gray-700 mb-4">
                      <li>Equipment must not be used in winds over 15 mph, rain, or lightning.</li>
                      <li>If weather conditions change during the rental, Customer must immediately deflate and stop use.</li>
                    </ul>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">10. Indemnification</h4>
                    <p className="text-gray-700 mb-4">Customer agrees to indemnify and hold harmless Bounce Dads ATL (LLH Party Rentals), its owners, agents, and employees from any and all claims, actions, suits, damages, liabilities, and expenses arising from or related to the use of the rental equipment.</p>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">11. Governing Law</h4>
                    <p className="text-gray-700 mb-4">This agreement shall be governed by and construed in accordance with the laws of the State of Georgia.</p>
                    
                    <h4 className="font-semibold text-gray-900 mt-4 mb-2">12. Entire Agreement</h4>
                    <p className="text-gray-700 mb-4">This document constitutes the entire agreement between the parties. No oral modifications or representations are valid unless in writing and signed by both parties.</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowTerms(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setAcceptedTerms(true);
                      setShowTerms(false);
                      setValidationErrors({ ...validationErrors, terms: '' });
                    }}
                    className="px-4 py-2 text-white rounded-md"
                    style={{ backgroundColor: theme.colors.primary[800] }}
                  >
                    Accept Terms
                  </button>
                </div>
              </div>
            </div>
          )}
            </>
          ) : (
            <>
              <div id="payment-form-section" className="mb-6">
                <button
                  onClick={() => setShowPayment(false)}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  {getContent('booking.title')}
                </button>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Information</h2>
              
              <StripePaymentForm
                amount={payInFull ? calculateTotalPrice() : calculateTotalPrice() * (depositPercentage / 100)}
                customerEmail={formData.customer_email}
                customerName={formData.customer_name}
                payInFull={payInFull}
                depositPercentage={depositPercentage}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;
